import React, { useState, useEffect, useCallback } from "react";
import { Input } from "antd";
import type { InputProps } from "antd";

type InputDebounceProps = Omit<InputProps, "onChange"> & {
  delay?: number;
  onChange?: (value: string) => void;
  onDebouncedChange?: (value: string) => void;
};

const InputDebounce: React.FC<InputDebounceProps> = ({
  delay = 800,
  onChange,
  onDebouncedChange,
  value: propValue,
  defaultValue,
  ...restProps
}) => {
  const [internalValue, setInternalValue] = useState<string>(
    propValue?.toString() || defaultValue?.toString() || ""
  );
  const [debouncedValue, setDebouncedValue] = useState<string>(internalValue);

  // Update internal value when prop value changes
  useEffect(() => {
    if (propValue !== undefined) {
      setInternalValue(propValue.toString());
    }
  }, [propValue]);

  // Debounce effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(internalValue);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [internalValue, delay]);

  // Call onDebouncedChange when debouncedValue changes
  useEffect(() => {
    if (
      onDebouncedChange &&
      debouncedValue !== (propValue?.toString() || defaultValue?.toString())
    ) {
      onDebouncedChange(debouncedValue);
    }
  }, [debouncedValue, onDebouncedChange, propValue, defaultValue]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInternalValue(newValue);

      // Call immediate onChange if provided
      if (onChange) {
        onChange(newValue);
      }
    },
    [onChange]
  );

  return <Input {...restProps} value={internalValue} onChange={handleChange} />;
};

export default InputDebounce;
